'use client';

import { c as _c } from "react/compiler-runtime";
import type Hls from 'hls.js';
import Image from 'next/image';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import heroFallbackStill from '@/assets/images/hero-fallback-still.jpg';
import { Icon } from '@/components/icon';
import { cn } from '@/lib/utils';
import { type AutoplayResult, handleVideoAutoplay, type VideoState } from './bunny-video-utils';
interface BunnyNetVideoContextType {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  videoState: VideoState;
  showPlayButton: boolean;
  playVideo: () => void;
  videoUrls: {
    hls: string;
    mp4Fallback: string;
    poster: string;
  };
}
const BunnyNetVideoContext = createContext<BunnyNetVideoContextType | null>(null);
function useBunnyNetVideo() {
  const context = useContext(BunnyNetVideoContext);
  if (!context) {
    throw new Error("BunnyNetVideo compound components must be used within BunnyNetVideo");
  }
  return context;
}
interface BunnyNetVideoProps {
  children: React.ReactNode;
  videoUrls: {
    hls: string;
    mp4Fallback: string;
    poster: string;
  };
}
export function BunnyVideoPlayer(t0) {
  const $ = _c(12);
  const {
    children,
    videoUrls
  } = t0;
  const [videoState, setVideoState] = useState("loading");
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const showPlayButton = videoState === "blocked";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = result => {
      setVideoState(result.state);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleStateChange = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      setVideoState("loading");
      video.play().then(() => setVideoState("playing")).catch(() => setVideoState("blocked"));
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const playVideo = t2;
  let t3;
  let t4;
  if ($[2] !== videoUrls) {
    t3 = () => {
      const video_0 = videoRef.current;
      if (!video_0) {
        return;
      }
      const cleanup = handleVideoAutoplay(video_0, videoUrls, handleStateChange, hlsRef);
      return cleanup;
    };
    t4 = [videoUrls];
    $[2] = videoUrls;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== showPlayButton || $[6] !== videoState || $[7] !== videoUrls) {
    t5 = {
      videoRef,
      videoState,
      showPlayButton,
      playVideo,
      videoUrls
    };
    $[5] = showPlayButton;
    $[6] = videoState;
    $[7] = videoUrls;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const value = t5;
  let t6;
  if ($[9] !== children || $[10] !== value) {
    t6 = <BunnyNetVideoContext.Provider value={value}>{children}</BunnyNetVideoContext.Provider>;
    $[9] = children;
    $[10] = value;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
interface BackgroundProps {
  className?: string;
  fallbackImage?: string;
  alt?: string;
}
export function BunnyVideo(t0) {
  const $ = _c(21);
  const {
    className,
    fallbackImage,
    alt: t1
  } = t0;
  const alt = t1 === undefined ? "Bunny Video" : t1;
  const {
    videoRef,
    videoState,
    videoUrls
  } = useBunnyNetVideo();
  const [imageError, setImageError] = useState(false);
  const showVideo = videoState === "playing";
  const showThumbnail = (videoState === "blocked" || videoState === "loading") && !imageError;
  const showFallbackImage = videoState === "failed" || imageError;
  let t2;
  if ($[0] !== className) {
    t2 = cn("relative size-full overflow-hidden bg-black", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = videoUrls.poster;
  const t4 = showVideo ? "opacity-100" : "opacity-0";
  let t5;
  if ($[2] !== t4) {
    t5 = cn("size-full object-cover object-top transition-opacity duration-1000", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t5 || $[5] !== videoRef || $[6] !== videoUrls.poster) {
    t6 = <video ref={videoRef} muted={true} loop={true} playsInline={true} autoPlay={true} poster={t3} className={t5} />;
    $[4] = t5;
    $[5] = videoRef;
    $[6] = videoUrls.poster;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== alt || $[9] !== showThumbnail || $[10] !== videoUrls.poster) {
    t7 = showThumbnail && <Image src={videoUrls.poster} alt={alt} fill={true} className="object-cover object-top" priority={true} unoptimized={true} onError={() => setImageError(true)} />;
    $[8] = alt;
    $[9] = showThumbnail;
    $[10] = videoUrls.poster;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== alt || $[13] !== fallbackImage || $[14] !== showFallbackImage) {
    t8 = showFallbackImage && <Image src={fallbackImage || heroFallbackStill} alt={alt} fill={true} className="object-cover object-top" priority={true} />;
    $[12] = alt;
    $[13] = fallbackImage;
    $[14] = showFallbackImage;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t2 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = <div className={t2}>{t6}{t7}{t8}</div>;
    $[16] = t2;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
interface PlayButtonProps {
  className?: string;
}
export function BunnyPlayButton(t0) {
  const $ = _c(14);
  const {
    className
  } = t0;
  const {
    showPlayButton,
    playVideo,
    videoState
  } = useBunnyNetVideo();
  if (!showPlayButton) {
    return null;
  }
  const isLoading = videoState === "loading";
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex items-center justify-center", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isLoading && "animate-pulse";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("flex items-center justify-center rounded-full bg-black/70 p-6 transition-all hover:bg-black/80 disabled:opacity-50", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isLoading) {
    t4 = isLoading ? <div className="h-12 w-12 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <Icon name="play" className="h-12 w-12 fill-white text-white" />;
    $[4] = isLoading;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isLoading || $[7] !== playVideo || $[8] !== t3 || $[9] !== t4) {
    t5 = <button type="button" onClick={playVideo} disabled={isLoading} className={t3} aria-label="Play video">{t4}</button>;
    $[6] = isLoading;
    $[7] = playVideo;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t5) {
    t6 = <div className={t1}>{t5}</div>;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}